import loadable from '@loadable/component';
import { AspectRatioType } from '@models/IAspectRatio';
import { IVideoAttributes } from '@models/IVideoAttributes';
import { IVideoSubtitle } from '@models/IVideoSubtitle';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';
import { MotionValue, motion } from 'framer-motion';
import {
  ChangeEvent,
  ForwardedRef,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import { Config } from 'react-player';

const ReactPlayer = loadable(
  () => import(/* webpackChunkName: "react-player" */ 'react-player')
);

interface Props {
  readonly classes?: string;
  readonly config?: Config;
  readonly controls?: boolean;
  readonly epiEdit?: string;
  readonly isPlaying?: boolean;
  readonly subtitles?: IVideoSubtitle[];
  readonly title?: string;
  readonly url?: string;
  readonly videoAttributes?: IVideoAttributes;
  readonly videoRatio?: AspectRatioType;
  readonly videoSource?: string;
  readonly videoWrapperOpacityProgress?: MotionValue<number>;
  readonly onEndedVideo?: () => void;
  readonly onVideoReady?: () => void;
  readonly onVideoStart?: () => void;
  readonly onVolumeChange?: (videoElement: HTMLVideoElement) => void;
  readonly setIsPlaying?: (playing: boolean) => void;
}

const Video = forwardRef(
  (
    {
      classes,
      config,
      controls = true,
      epiEdit,
      isPlaying,
      title,
      subtitles,
      videoAttributes,
      videoRatio = 'aspect-video',
      videoSource,
      videoWrapperOpacityProgress,
      onEndedVideo,
      onVideoReady,
      onVideoStart,
      onVolumeChange,
      setIsPlaying,
    }: Props,
    ref
  ) => {
    const [prevProgress, setPrevProgress] = useState(0);
    const [isBrowser, setIsBrowser] = useState(false);

    useEffect(() => {
      setIsBrowser(typeof window !== 'undefined');
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
    useEffect(() => {
      setPrevProgress(0);
    }, [title]);

    const handleProgressUpdate = (progress: string) => {
      updateDataLayer({
        event: 'videoInteraction',
        video_interaction: 'video_progress',
        video_progress: progress,
        video_title: title,
      });
    };

    const handleVideoAction = (action: string) => {
      updateDataLayer({
        event: 'videoInteraction',
        video_interaction:
          action === 'START' ? 'video_start' : 'video_complete',
        video_progress: action === 'START' ? 'video_start' : 'video_complete',
        video_title: title,
      });
    };

    const updateVideoProgressData = (progress: number) => {
      if (prevProgress < 0.25 && progress > 0.25) {
        handleProgressUpdate('25%');
      }
      if (prevProgress < 0.5 && progress > 0.5) {
        handleProgressUpdate('50%');
      }
      if (prevProgress < 0.75 && progress > 0.75) {
        handleProgressUpdate('75%');
      }
      if (prevProgress > 0.9 && progress < 0.1) {
        handleVideoAction('COMPLETE');
      }
      setPrevProgress(progress);
    };

    if (!videoSource) return null;

    return (
      <motion.div
        ref={ref as ForwardedRef<HTMLDivElement>}
        className={classNames(classes, videoRatio, 'w-full')}
        data-epi-property-name={epiEdit}
        data-testid={isPlaying ? 'video-playing' : 'video-paused'}
        style={{
          opacity: videoWrapperOpacityProgress ?? 1,
        }}
      >
        {isBrowser && (
          <ReactPlayer
            url={videoSource}
            width="100%"
            height="100%"
            controls={controls}
            playing
            onProgress={({ played }) => updateVideoProgressData(played)}
            onStart={() => {
              onVideoStart?.();
              handleVideoAction('START');
            }}
            onEnded={() => {
              onEndedVideo?.();
              handleVideoAction('COMPLETE');
            }}
            onPause={() => setIsPlaying?.(false)}
            onPlay={() => setIsPlaying?.(true)}
            onReady={onVideoReady}
            config={{
              ...config,
              file: {
                tracks: subtitles || [],
              },
            }}
            onVolumeChange={(event: ChangeEvent<HTMLVideoElement>) => {
              onVolumeChange?.(event.target);
            }}
            {...videoAttributes}
          />
        )}
      </motion.div>
    );
  }
);

export default Video;
